import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import { useFamily } from '../../contexts/FamilyContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import Switch from '../ui/Switch'
import toast from 'react-hot-toast'

const { FiX, FiDownload, FiDatabase, FiCheck, FiMail } = FiIcons

const ExportDataModal = ({ isOpen, onClose }) => {
  const { family } = useAuth()
  const { tasks, completions, rewards } = useFamily()
  const [loading, setLoading] = useState(false)
  const [exportOptions, setExportOptions] = useState({
    family_info: true,
    children_profiles: true,
    tasks: true,
    completions: true,
    rewards: true,
    analytics: false,
    format: 'json'
  })
  const [deliveryMethod, setDeliveryMethod] = useState('download')

  const handleOptionChange = (option, value) => {
    setExportOptions(prev => ({
      ...prev,
      [option]: value
    }))
  }

  const generateExportData = () => {
    const exportData = {}

    if (exportOptions.family_info) {
      exportData.family = {
        name: family.name,
        created_at: family.created_at,
        settings: family.settings
      }
    }

    if (exportOptions.children_profiles) {
      exportData.children = family.children?.map(child => ({
        name: child.name,
        age: child.age,
        avatar: child.avatar,
        created_at: child.created_at
      })) || []
    }

    if (exportOptions.tasks) {
      exportData.tasks = tasks.map(task => ({
        title: task.title,
        description: task.description,
        category: task.category,
        points: task.points,
        estimated_time_minutes: task.estimated_time_minutes,
        recurring_pattern: task.recurring_pattern,
        created_at: task.created_at
      }))
    }

    if (exportOptions.completions) {
      exportData.completions = completions.map(completion => ({
        task_title: completion.tasks?.title,
        child_name: completion.children?.name,
        points_earned: completion.points_earned,
        completed_at: completion.completed_at
      }))
    }

    if (exportOptions.rewards) {
      exportData.rewards = rewards.map(reward => ({
        title: reward.title,
        description: reward.description,
        points_required: reward.points_required,
        emoji: reward.emoji,
        created_at: reward.created_at
      }))
    }

    exportData.export_info = {
      generated_at: new Date().toISOString(),
      family_name: family.name,
      export_version: '1.0'
    }

    return exportData
  }

  const downloadFile = (data, filename) => {
    const blob = new Blob([data], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleExport = async () => {
    setLoading(true)
    
    try {
      const exportData = generateExportData()
      const timestamp = new Date().toISOString().split('T')[0]
      const filename = `happy-hub-${family.name.toLowerCase().replace(/\s+/g, '-')}-${timestamp}.json`

      if (deliveryMethod === 'download') {
        const jsonData = JSON.stringify(exportData, null, 2)
        downloadFile(jsonData, filename)
        toast.success('Data exported successfully! 📁')
      } else {
        // Simulate email delivery
        toast.success('Export request submitted! You\'ll receive an email with your data within 24 hours. 📧')
      }

      onClose()
    } catch (error) {
      console.error('Export error:', error)
      toast.error('Failed to export data. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const getDataSize = () => {
    const data = generateExportData()
    const jsonString = JSON.stringify(data)
    const sizeInBytes = new Blob([jsonString]).size
    const sizeInKB = Math.round(sizeInBytes / 1024)
    return sizeInKB < 1024 ? `${sizeInKB} KB` : `${Math.round(sizeInKB / 1024)} MB`
  }

  const exportSections = [
    {
      key: 'family_info',
      title: 'Family Information',
      description: 'Family name, settings, and account details',
      icon: FiDatabase,
      color: 'text-blue-500'
    },
    {
      key: 'children_profiles',
      title: 'Children Profiles',
      description: 'Names, ages, avatars, and profile information',
      icon: FiDatabase,
      color: 'text-green-500'
    },
    {
      key: 'tasks',
      title: 'Tasks & Chores',
      description: 'All created tasks with details and settings',
      icon: FiDatabase,
      color: 'text-purple-500'
    },
    {
      key: 'completions',
      title: 'Completion History',
      description: 'Record of all completed tasks and points earned',
      icon: FiDatabase,
      color: 'text-orange-500'
    },
    {
      key: 'rewards',
      title: 'Rewards Catalog',
      description: 'All rewards and redemption information',
      icon: FiDatabase,
      color: 'text-pink-500'
    },
    {
      key: 'analytics',
      title: 'Analytics Data',
      description: 'Usage statistics and progress analytics',
      icon: FiDatabase,
      color: 'text-gray-500'
    }
  ]

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiDownload} className="text-blue-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Export Family Data</h2>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="p-2"
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </Button>
              </div>

              <div className="space-y-6">
                {/* Export Options */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    What would you like to export?
                  </h3>
                  <div className="space-y-3">
                    {exportSections.map((section) => (
                      <div
                        key={section.key}
                        className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          <SafeIcon icon={section.icon} className={`${section.color} text-lg`} />
                          <div>
                            <div className="font-medium text-gray-900">{section.title}</div>
                            <div className="text-sm text-gray-500">{section.description}</div>
                          </div>
                        </div>
                        <Switch
                          checked={exportOptions[section.key]}
                          onChange={(checked) => handleOptionChange(section.key, checked)}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Format Options */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Export Format
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleOptionChange('format', 'json')}
                      className={`
                        p-4 rounded-lg border-2 transition-all duration-200 text-center
                        ${exportOptions.format === 'json'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                        }
                      `}
                    >
                      <div className="font-medium">JSON</div>
                      <div className="text-sm text-gray-500">Machine readable</div>
                    </button>
                    <button
                      onClick={() => handleOptionChange('format', 'csv')}
                      className={`
                        p-4 rounded-lg border-2 transition-all duration-200 text-center opacity-50 cursor-not-allowed
                        ${exportOptions.format === 'csv'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200'
                        }
                      `}
                      disabled
                    >
                      <div className="font-medium">CSV</div>
                      <div className="text-sm text-gray-500">Coming soon</div>
                    </button>
                  </div>
                </div>

                {/* Delivery Method */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Delivery Method
                  </h3>
                  <div className="space-y-3">
                    <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="delivery"
                        value="download"
                        checked={deliveryMethod === 'download'}
                        onChange={(e) => setDeliveryMethod(e.target.value)}
                        className="mr-3"
                      />
                      <div className="flex items-center gap-3">
                        <SafeIcon icon={FiDownload} className="text-blue-500" />
                        <div>
                          <div className="font-medium">Instant Download</div>
                          <div className="text-sm text-gray-500">Download immediately to your device</div>
                        </div>
                      </div>
                    </label>
                    
                    <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="delivery"
                        value="email"
                        checked={deliveryMethod === 'email'}
                        onChange={(e) => setDeliveryMethod(e.target.value)}
                        className="mr-3"
                      />
                      <div className="flex items-center gap-3">
                        <SafeIcon icon={FiMail} className="text-green-500" />
                        <div>
                          <div className="font-medium">Email Delivery</div>
                          <div className="text-sm text-gray-500">Receive secure download link via email</div>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Export Summary */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">Export Summary</h4>
                  <div className="text-sm text-blue-700 space-y-1">
                    <div>• Family: {family.name}</div>
                    <div>• Children: {family.children?.length || 0}</div>
                    <div>• Tasks: {tasks.length}</div>
                    <div>• Completions: {completions.length}</div>
                    <div>• Estimated size: {getDataSize()}</div>
                  </div>
                </div>

                {/* Privacy Notice */}
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <SafeIcon icon={FiCheck} className="text-yellow-600 text-lg mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-yellow-800 mb-1">Privacy Notice</h4>
                      <p className="text-sm text-yellow-700">
                        Your exported data will only include information belonging to your family. 
                        Sensitive information like passwords are never included in exports.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onClose}
                    className="flex-1"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleExport}
                    loading={loading}
                    className="flex-1"
                    disabled={!Object.values(exportOptions).some(v => v === true)}
                  >
                    <SafeIcon icon={FiDownload} className="text-sm" />
                    {deliveryMethod === 'download' ? 'Download Data' : 'Request Export'}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default ExportDataModal